import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
    'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all [&>svg]:pointer-events-none [&>svg]:size-3!',
    {
        variants: {
            variant: {
                default: 'bg-neutral-100 text-text-secondary dark:bg-bg-raised',
                primary: 'bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
                success: 'bg-success-50 text-success-700 dark:bg-success-900 dark:text-success-300',
                warning: 'bg-warning-50 text-warning-700 dark:bg-warning-900 dark:text-warning-300',
                danger: 'bg-error-50 text-error-700 dark:bg-error-900 dark:text-error-300',
                info: 'bg-info-50 text-info-700 dark:bg-info-900 dark:text-info-300',
                outline: 'border-border-default text-text-primary',
                destructive: 'bg-error-50 text-error-700 dark:bg-error-900 dark:text-error-300',
                // The two hues the semantic ramps don't cover. They read from the
                // `--tag-*` tokens, which `index.css` already redefines under
                // `.dark` — so unlike the ramps above these need no `dark:` pair.
                // Same bridge `components/ui/section-tone.ts` makes for icon tiles.
                teal: 'bg-(--tag-teal-bg) text-(--tag-teal-color)',
                purple: 'bg-(--tag-purple-bg) text-(--tag-purple-color)',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

/**
 * The variant union, derived from the CVA rather than hand-written.
 *
 * Nine files across the app re-declared this by hand, each with a different
 * subset, so adding a variant silently left them behind. Import this instead.
 */
export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

function Badge({
    className,
    variant = 'default',
    asChild = false,
    ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
    const Comp = asChild ? Slot.Root : 'span';

    return (
        <Comp
            data-slot="badge"
            data-variant={variant}
            className={cn(badgeVariants({ variant }), className)}
            {...props}
        />
    );
}

function BadgeDot({ className, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="badge-dot"
            className={cn('size-1.5 shrink-0 rounded-full bg-current', className)}
            {...props}
        />
    );
}

export { Badge, BadgeDot, badgeVariants };
